
import { defineComponent, onBeforeMount, PropType, reactive, watchEffect } from 'vue'

// components
import Loader from '@/common/components/UI/Loader/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'

// module components
import OrdersListItem from '@/modules/DispatcherModule/components/OrdersListItem/index.vue'
import OrderCard from '@/modules/DispatcherModule/components/OrderCard/index.vue'

// types
import {
    DispatcherTakeOrderCardT,
    DispatcherTakeOrderT,
    WaitListStatus,
} from '@/core/types/dispatcher.types'
import { CompanyT } from '@/core/types/companies.types'
import { CatchedErrorStateT } from '@/core/types/error.types'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisations
import { companies } from '@/core/realisations/companies.realisation'
import { dispatcher } from '@/core/realisations/dispatcher.realisation'

// composable
import { useDebounce } from 'vue-composable'

export default defineComponent({
    components: {
        loader: Loader,
        'the-button': TheButton,
        'the-input': TheInput,
        'the-select': TheSelect,
        'failed-fetch': FailedFetch,
        'orders-list-item': OrdersListItem,
        'order-card': OrderCard,
    },
    props: {
        listLoading: {
            type: Boolean,
            default: false,
        },
        cardLoading: {
            type: Boolean,
            default: false,
        },
        orders: {
            type: Array as PropType<DispatcherTakeOrderT[]>,
            default: () => [],
        },
        checkedOrdersIds: {
            type: Array as PropType<number[]>,
            default: () => [],
        },
        selectedOrder: {
            type: Number,
            default: null,
        },
        orderNumberOneC: {
            type: String,
            default: null,
        },
        card: {
            type: Object as PropType<DispatcherTakeOrderCardT>,
            default: null,
        },
        cardFailed: {
            type: Boolean,
            default: false,
        },
        cardErrorState: {
            type: Object as PropType<CatchedErrorStateT>,
            default: null,
        },
        waitListStatusIds: {
            type: Array as PropType<number[]>,
            default: () => [],
        },
        excludeCompaniesIds: {
            type: Array as PropType<number[]>,
            default: () => [],
        },
        includeCompaniesIds: {
            type: Array as PropType<number[]>,
            default: () => [],
        },
    },
    emits: [
        'select',
        'refresh-card',
        'start-submit-orders',
        'start-submit-order',
        'start-submit-checked-orders',
        'exclude-companies',
        'include-companies',
        'wait-statuses-ids',
        'search-by-1c',
        'refresh',
        'date-changed',
        'check-order',
    ],
    setup(props, { emit }) {
        const state = reactive({
            orders: [] as DispatcherTakeOrderT[],
            orders_copy: [] as DispatcherTakeOrderT[],
            search_order_query: null as string | null,
            status_ids: [1, 2] as number[],
        })

        const emitSelect = (order_id: number) => {
            emit('select', order_id)
        }

        const emitRefreshCard = () => {
            emit('refresh-card')
        }

        const emitStartSubmitOrders = () => {
            emit('start-submit-orders')
        }

        const emitStartSubmitOrder = () => {
            emit('start-submit-order')
        }

        const emitStartSubmitCheckedOrders = () => {
            emit('start-submit-checked-orders')
        }

        const data_state = reactive({
            companies_include: new DataState<CompanyT>({}),
            companies: new DataState<CompanyT>({}),
            wait_list_statuses: new DataState<WaitListStatus>({}),
        })

        const loadWaitListStatuses = async () => {
            try {
                data_state.wait_list_statuses.is_loading = true

                const response = await dispatcher.loadWaitListStatuses(1)
                data_state.wait_list_statuses.list = response.data

                data_state.wait_list_statuses.is_failed = false
                data_state.wait_list_statuses.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.wait_list_statuses.failure.catchError(error)

                if (!data_state.wait_list_statuses.failure.error_state.is_cancel) {
                    data_state.wait_list_statuses.is_failed = true
                }
            } finally {
                if (!data_state.wait_list_statuses.failure.error_state.is_cancel) {
                    data_state.wait_list_statuses.is_loading = false
                }
            }
        }

        const loadCompaniesInitial = async () => {
            data_state.companies.pagination.current_page = 1
            data_state.companies_include.pagination.current_page = 1

            try {
                data_state.companies.is_loading = true
                data_state.companies_include.is_loading = true

                const response = await companies.getList({ page: 1 })
                data_state.companies.list = response.data
                data_state.companies_include.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page
                data_state.companies_include.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false

                data_state.companies_include.is_failed = false
                data_state.companies_include.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)
                data_state.companies_include.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
                if (!data_state.companies_include.failure.error_state.is_cancel) {
                    data_state.companies_include.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
                if (!data_state.companies_include.failure.error_state.is_cancel) {
                    data_state.companies_include.is_loading = false
                }
            }
        }

        const loadCompanies = async (page: number) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({ page })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadCompaniesInclude = async (page: number, search?: string) => {
            data_state.companies_include.pagination.current_page = page

            try {
                data_state.companies_include.is_loading = true
                const response = await companies.getList({ page, search })
                data_state.companies_include.list = response.data

                data_state.companies_include.pagination.total_pages = response.meta.last_page

                data_state.companies_include.is_failed = false
                data_state.companies_include.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies_include.failure.catchError(error)

                if (!data_state.companies_include.failure.error_state.is_cancel) {
                    data_state.companies_include.is_failed = true
                }
            } finally {
                if (!data_state.companies_include.failure.error_state.is_cancel) {
                    data_state.companies_include.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({ page })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompaniesInclude = async (page: number) => {
            if (page > data_state.companies_include.pagination.total_pages) return
            data_state.companies_include.pagination.current_page = page

            try {
                data_state.companies_include.is_loading = true

                const response = await companies.getList({ page })
                data_state.companies_include.list = data_state.companies_include.list.concat(
                    response.data
                )

                data_state.companies_include.pagination.total_pages = response.meta.last_page

                data_state.companies_include.is_failed = false
                data_state.companies_include.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies_include.failure.catchError(error)

                if (!data_state.companies_include.failure.error_state.is_cancel) {
                    data_state.companies_include.is_failed = true
                }
            } finally {
                if (!data_state.companies_include.failure.error_state.is_cancel) {
                    data_state.companies_include.is_loading = false
                }
            }
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        const unshiftFindedCompanyInclude = async (company: CompanyT) => {
            await loadCompaniesInclude(data_state.companies_include.pagination.current_page)
            data_state.companies_include.list.unshift(company)
        }

        onBeforeMount(async () => {
            await loadWaitListStatuses()
            await loadCompaniesInitial()

            if (props.orders.length) {
                state.orders = props.orders
                state.orders_copy = props.orders
            }
        })

        watchEffect(() => {
            state.orders = props.orders
            state.orders_copy = props.orders
            state.search_order_query = props.orderNumberOneC
        })

        const searchOrder = useDebounce(() => {
            const query = state.search_order_query?.toLowerCase()

            if (query) {
                emit('search-by-1c', query)
                // state.orders_copy = state.orders.filter((order) => {
                //     return String(order.id).includes(query)
                // })
            } else {
                state.orders_copy = props.orders
            }
        }, 800)

        const emitExcludeCompanies = (company_ids: number[]) => {
            emit('exclude-companies', company_ids)
        }

        const emitIncludeCompanies = (company_ids: number[]) => {
            emit('include-companies', company_ids)
        }

        const emitWaitListStatus = (statuses_ids: number[]) => {
            emit('wait-statuses-ids', statuses_ids)
        }

        const emitRefresh = () => {
            state.search_order_query = null
            emit('search-by-1c', null)
            emit('include-companies', [])
            emit('exclude-companies', [])
            emit('wait-statuses-ids', [])

            emit('refresh')
        }

        const emitDateChanged = () => {
            emit('date-changed')
        }

        const emitCheck = (id: number) => {
            emit('check-order', id)
        }

        const checkIfChecked = (id: number) => {
            return props.checkedOrdersIds.includes(id)
        }

        return {
            state,
            emitSelect,
            emitRefreshCard,
            emitStartSubmitOrders,
            emitStartSubmitOrder,
            emitStartSubmitCheckedOrders,
            data_state,
            // exclude
            loadWaitListStatuses,
            loadCompanies,
            loadMoreCompanies,
            unshiftFindedCompany,
            // include
            loadCompaniesInclude,
            loadMoreCompaniesInclude,
            unshiftFindedCompanyInclude,
            searchOrder,
            emitExcludeCompanies,
            emitIncludeCompanies,
            emitWaitListStatus,
            emitRefresh,
            emitDateChanged,
            emitCheck,
            checkIfChecked,
        }
    },
})
